import React, { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Process } from '../components/Process';
import { ConsultationForm } from '../components/ConsultationForm';
import { Shield, CheckCircle2, X, FileText, Scale, Award, Star } from 'lucide-react';
import { testimonials } from '../data/mock';
import { trademarkPricingTiers } from '../data/competitivePricing';

export const TrademarkRegistration = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  const openForm = (formType) => {
    setIsFormOpen(true);
  };

  const pricingPlans = trademarkPricingTiers;

  const serviceTestimonials = testimonials.filter(t => [1, 2, 3].includes(t.id));

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <ConsultationForm 
        isOpen={isFormOpen} 
        onClose={() => setIsFormOpen(false)}
        formType="registration"
      />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#0A1F44] to-[#153356] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-4 py-2 mb-6">
              <Shield className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm font-medium">Trademark Search & Registration</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Protect Your Brand Identity
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Secure exclusive rights to your brand name and logo with expert trademark registration services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#pricing" className="liquid inline-block bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1F44] font-bold py-4 px-8 rounded-lg text-lg whitespace-nowrap">
                View Pricing
              </a>
              <button 
                onClick={() => openForm('consultation')}
                className="liquid-white inline-block border-2 border-white text-white hover:bg-white hover:text-[#0A1F44] font-bold py-4 px-8 rounded-lg text-lg whitespace-nowrap"
              >
                Free Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What is Trademark Search & Registration */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wider">What is Trademark Search & Registration</span>
              <h2 className="text-4xl font-bold text-[#0A1F44] mt-4 mb-6">
                Your Brand's Legal Shield
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                A trademark is a unique symbol, word, phrase, or design that identifies and distinguishes your products or services from others. Before registration, we conduct comprehensive trademark searches to ensure your mark is available and doesn't conflict with existing trademarks. This crucial step helps avoid costly legal disputes and application rejections.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Once cleared, trademark registration with the USPTO (United States Patent and Trademark Office) grants you exclusive legal rights to use this mark in commerce and prevents others from using similar marks that could confuse consumers. You gain nationwide protection and the legal presumption of ownership—a powerful legal tool that helps you build brand recognition, maintain market position, and protect your business investment.
              </p>
              <div className="bg-[#D4AF37]/10 border-l-4 border-[#D4AF37] p-6 rounded">
                <p className="text-[#0A1F44] font-semibold">
                  "A registered trademark is one of the most valuable assets a business can own, providing lasting protection for your brand identity."
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-[#0A1F44] mb-6">What a Trademark Protects</h3>
              
              {/* Accordion Items - Using radio buttons for exclusive opening */}
              <input type="radio" name="accordion" id="accordion-1" className="hidden accordion-radio" defaultChecked />
              <div className="accordion-item bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-[#D4AF37] transition-all">
                <label htmlFor="accordion-1" className="flex justify-between items-center p-6 cursor-pointer">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-[#0A1F44]" />
                    </div>
                    <h4 className="text-xl font-bold text-[#0A1F44]">Brand Names</h4>
                  </div>
                  <svg className="w-6 h-6 text-[#D4AF37] accordion-icon transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </label>
                <div className="accordion-content px-6 pb-6 text-gray-600 leading-relaxed">
                  Your company name, product names, and service names are crucial identifiers in the marketplace. Registering these as trademarks ensures no competitor can use a confusingly similar name, protecting your business reputation and customer relationships. This includes variations, abbreviations, and stylized versions of your brand name.
                </div>
              </div>

              <input type="radio" name="accordion" id="accordion-2" className="hidden accordion-radio" />
              <div className="accordion-item bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-[#D4AF37] transition-all">
                <label htmlFor="accordion-2" className="flex justify-between items-center p-6 cursor-pointer">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                      <Award className="w-6 h-6 text-[#0A1F44]" />
                    </div>
                    <h4 className="text-xl font-bold text-[#0A1F44]">Logos & Designs</h4>
                  </div>
                  <svg className="w-6 h-6 text-[#D4AF37] accordion-icon transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </label>
                <div className="accordion-content px-6 pb-6 text-gray-600 leading-relaxed">
                  Visual brand identifiers like logos, graphic designs, and distinctive symbols are the face of your brand. Trademark protection prevents others from copying or creating similar designs that could confuse your customers. This includes color combinations, layout designs, and any unique visual elements that distinguish your brand in the market.
                </div>
              </div>

              <input type="radio" name="accordion" id="accordion-3" className="hidden accordion-radio" />
              <div className="accordion-item bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-[#D4AF37] transition-all">
                <label htmlFor="accordion-3" className="flex justify-between items-center p-6 cursor-pointer">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                      <Scale className="w-6 h-6 text-[#0A1F44]" />
                    </div>
                    <h4 className="text-xl font-bold text-[#0A1F44]">Slogans & Taglines</h4>
                  </div>
                  <svg className="w-6 h-6 text-[#D4AF37] accordion-icon transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </label>
                <div className="accordion-content px-6 pb-6 text-gray-600 leading-relaxed">
                  Memorable catchphrases and brand messaging create emotional connections with your audience. Protecting your slogans ensures that competitors cannot capitalize on the marketing investment you've made. Whether it's "Just Do It" level famous or a local favorite, your tagline deserves legal protection to maintain its unique association with your brand.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Register a Trademark - Slider */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wider">Benefits</span>
            <h2 className="text-4xl font-bold text-[#0A1F44] mt-4 mb-6">
              Why Register Your Trademark?
            </h2>
          </div>
          
          {/* Benefits Slider */}
          <div className="relative overflow-hidden">
            <div className="flex gap-6 animate-scroll hover:pause-animation">
              {[
                { icon: Shield, title: 'Legal Protection', desc: 'Gain exclusive nationwide rights and legal presumption of ownership with federal registration.' },
                { icon: Award, title: 'Brand Value', desc: 'Increase your brand\'s value and credibility with official trademark registration status.' },
                { icon: Scale, title: 'Legal Recourse', desc: 'Sue infringers in federal court and recover damages, attorney fees, and legal costs.' },
                { icon: CheckCircle2, title: 'Market Exclusivity', desc: 'Prevent competitors from using confusingly similar marks in your industry and market.' },
                { icon: FileText, title: 'Asset Building', desc: 'Create a valuable intangible asset that can be licensed, sold, or used as collateral.' },
                { icon: Shield, title: 'National Coverage', desc: 'Protect your brand across all 50 states with a single federal registration.' },
                { icon: Award, title: 'Consumer Trust', desc: 'Build customer confidence with the ® symbol showing official trademark status.' },
                { icon: Scale, title: 'Domain Protection', desc: 'Stop cybersquatters and protect your brand in the digital marketplace.' }
              ].map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="flex-shrink-0 w-80 bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
                    <div className="w-14 h-14 bg-[#D4AF37] rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-7 h-7 text-[#0A1F44]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#0A1F44] mb-2">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{benefit.desc}</p>
                  </div>
                );
              })}
              {/* Duplicate for seamless loop */}
              {[
                { icon: Shield, title: 'Legal Protection', desc: 'Gain exclusive nationwide rights and legal presumption of ownership with federal registration.' },
                { icon: Award, title: 'Brand Value', desc: 'Increase your brand\'s value and credibility with official trademark registration status.' },
                { icon: Scale, title: 'Legal Recourse', desc: 'Sue infringers in federal court and recover damages, attorney fees, and legal costs.' },
                { icon: CheckCircle2, title: 'Market Exclusivity', desc: 'Prevent competitors from using confusingly similar marks in your industry and market.' }
              ].map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={`dup-${index}`} className="flex-shrink-0 w-80 bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
                    <div className="w-14 h-14 bg-[#D4AF37] rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-7 h-7 text-[#0A1F44]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#0A1F44] mb-2">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{benefit.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Modern CTA Section After Benefits - Fidabio Inspired */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Gradient Frame */}
          <div className="relative bg-gradient-to-br from-red-900/40 via-gray-900 to-red-900/40 rounded-3xl p-1">
            {/* Inner Black Container */}
            <div className="bg-black rounded-3xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 items-center">
                {/* Left Side - Abstract Geometric Graphic */}
                <div className="relative h-64 lg:h-80 overflow-hidden">
                  {/* Layered Semi-circles */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="relative w-64 h-64">
                      <div className="absolute inset-0 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full opacity-90"></div>
                      <div className="absolute inset-4 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full opacity-80"></div>
                      <div className="absolute inset-8 bg-gradient-to-br from-gray-500 to-gray-600 rounded-full opacity-70"></div>
                      <div className="absolute inset-12 bg-gradient-to-br from-gray-600 to-gray-700 rounded-full opacity-60"></div>
                      <div className="absolute inset-16 bg-gradient-to-br from-gray-700 to-gray-800 rounded-full opacity-50"></div>
                    </div>
                  </div>
                </div>

                {/* Right Side - Content */}
                <div className="px-8 py-12 lg:py-16">
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                    Learn More about Trademark Protection
                  </h2>
                  <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                    Your brand deserves protection. We're here to help you secure your trademark rights with expert guidance every step of the way.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button 
                      onClick={() => openForm('registration')}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-8 py-4 rounded-xl text-lg transition-all duration-300 whitespace-nowrap"
                    >
                      Register Trademark
                    </button>
                    <button 
                      onClick={() => openForm('consultation')}
                      className="border-2 border-white text-white hover:bg-white hover:text-black font-bold px-8 py-4 rounded-xl text-lg transition-all duration-300 whitespace-nowrap"
                    >
                      Free Consultation
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wider">Pricing</span>
            <h2 className="text-4xl font-bold text-[#0A1F44] mt-4 mb-6">
              Trademark Search & Registration Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the package that best fits your trademark protection needs.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl p-6 transition-all duration-300 flex flex-col ${
                  plan.popular
                    ? 'border-4 border-[#D4AF37] shadow-2xl transform lg:-translate-y-4'
                    : 'border-2 border-gray-200 shadow-lg hover:shadow-2xl hover:-translate-y-2'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-[#D4AF37] text-[#0A1F44] px-4 py-1 rounded-full font-bold text-sm flex items-center space-x-1 whitespace-nowrap">
                      <Star className="w-4 h-4 fill-current" />
                      <span>MOST POPULAR</span>
                    </div>
                  </div>
                )}
                <h3 className="text-2xl font-bold text-[#0A1F44] mb-2">{plan.name}</h3>
                <div className="mb-2">
                  {plan.wasPrice && (
                    <div className="text-lg text-gray-400 line-through mb-1">{plan.wasPrice}</div>
                  )}
                  <span className="text-4xl font-bold text-[#0A1F44]">{plan.price}</span>
                </div>
                {plan.priceNote && (
                  <p className="text-xs text-gray-500 mb-2">{plan.priceNote}</p>
                )}
                <p className="text-gray-600 text-sm mb-6">{plan.description}</p>
                <ul className="space-y-3 mb-6 flex-grow">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button 
                  onClick={() => openForm('registration')}
                  className={`w-full font-bold py-3 px-6 rounded-lg transition-all duration-300 mt-auto ${
                    plan.popular
                      ? 'liquid bg-[#D4AF37] text-[#0A1F44] hover:bg-[#B8941F]'
                      : 'liquid-outline bg-transparent text-[#0A1F44] border-2 border-[#0A1F44] hover:bg-[#0A1F44] hover:text-white'
                  }`}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <Process />

      {/* Trademark vs Copyright Table */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1F44] mb-6">
              Trademark vs. Copyright: What's the Difference?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding the distinction helps you choose the right protection for your intellectual property.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-2xl overflow-hidden shadow-lg">
              <thead>
                <tr className="bg-[#0A1F44]">
                  <th className="px-6 py-4 text-left text-white font-bold">Aspect</th>
                  <th className="px-6 py-4 text-left text-[#D4AF37] font-bold">Trademark</th>
                  <th className="px-6 py-4 text-left text-white font-bold">Copyright</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 font-semibold text-[#0A1F44]">Protects</td>
                  <td className="px-6 py-4 text-gray-600">Brand names, logos, slogans</td>
                  <td className="px-6 py-4 text-gray-600">Original creative works (books, music, art)</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-[#0A1F44]">Purpose</td>
                  <td className="px-6 py-4 text-gray-600">Identifies source of goods/services</td>
                  <td className="px-6 py-4 text-gray-600">Protects creative expression</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 font-semibold text-[#0A1F44]">Duration</td>
                  <td className="px-6 py-4 text-gray-600">10 years (renewable indefinitely)</td>
                  <td className="px-6 py-4 text-gray-600">Life of author + 70 years</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-[#0A1F44]">Registration</td>
                  <td className="px-6 py-4 text-gray-600">USPTO (United States Patent and Trademark Office)</td>
                  <td className="px-6 py-4 text-gray-600">U.S. Copyright Office</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 font-semibold text-[#0A1F44]">Symbol</td>
                  <td className="px-6 py-4 text-gray-600">® (registered) or ™ (unregistered)</td>
                  <td className="px-6 py-4 text-gray-600">© (copyright)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-[#0A1F44]">Use Required</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      <span className="text-gray-600">Yes, must use in commerce</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <X className="w-5 h-5 text-red-500" />
                      <span className="text-gray-600">No, automatic upon creation</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1F44] mb-6">
              What Our Clients Say
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {serviceTestimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-gray-50 rounded-2xl p-6">
                <p className="text-gray-700 mb-4">"{testimonial.content}"</p>
                <div className="flex items-center space-x-3">
                  <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <div className="font-bold text-[#0A1F44]">{testimonial.name}</div>
                    <div className="text-gray-600 text-sm">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Second CTA Before Footer - Fidabio Inspired */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Gradient Frame */}
          <div className="relative bg-gradient-to-br from-[#D4AF37]/30 via-[#0A1F44] to-[#D4AF37]/30 rounded-3xl p-1">
            {/* Inner Black Container */}
            <div className="bg-black rounded-3xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 items-center">
                {/* Left Side - Content */}
                <div className="px-8 py-12 lg:py-16 order-2 lg:order-1">
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                    Ready to Protect Your Brand?
                  </h2>
                  <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                    Connect with our trademark specialists today. Get expert guidance on securing your intellectual property and safeguarding your brand identity.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button 
                      onClick={() => openForm('registration')}
                      className="bg-gradient-to-r from-[#D4AF37] to-[#B8941F] hover:from-[#B8941F] hover:to-[#D4AF37] text-[#0A1F44] font-bold px-8 py-4 rounded-xl text-lg transition-all duration-300 whitespace-nowrap"
                    >
                      Register Trademark
                    </button>
                    <button 
                      onClick={() => openForm('consultation')}
                      className="border-2 border-white text-white hover:bg-white hover:text-[#0A1F44] font-bold px-8 py-4 rounded-xl text-lg transition-all duration-300 whitespace-nowrap"
                    >
                      Free Consultation
                    </button>
                  </div>
                </div>

                {/* Right Side - Abstract Geometric Graphic */}
                <div className="relative h-64 lg:h-80 overflow-hidden order-1 lg:order-2">
                  {/* Layered Semi-circles - Navy & Gold Theme */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="relative w-64 h-64">
                      <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37] to-[#B8941F] rounded-full opacity-30"></div>
                      <div className="absolute inset-4 bg-gradient-to-br from-[#D4AF37]/80 to-[#0A1F44] rounded-full opacity-40"></div>
                      <div className="absolute inset-8 bg-gradient-to-br from-[#0A1F44] to-[#153356] rounded-full opacity-50"></div>
                      <div className="absolute inset-12 bg-gradient-to-br from-[#153356] to-[#0A1F44] rounded-full opacity-60"></div>
                      <div className="absolute inset-16 bg-gradient-to-br from-[#0A1F44] to-[#1a3a5c] rounded-full opacity-70"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};